import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBookReader,
  faBooks,
  faMoneyBillWave,
} from '@fortawesome/pro-duotone-svg-icons';
import { Loading, Page, ShowMorePagination } from '@ebsco-ui/ebsco-ui';

import {
  EmptyRecordsMessage,
  PageErrorMessage,
  RecordSummary,
  RedirectToLoginButton,
  ResultCountWithSorting,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import {
  useHoldsAndCheckoutsPageConfig,
  usePaginationItemFocus,
  useQueryParams,
} from '@app/hooks';
import { generateRouteParams } from '@app/utils';
import { getPageMessages, sharedMessages } from '@app/translations';
import { HoldsAndCheckoutsTab, RECORDS_PER_PAGE } from '@app/constants';

import {
  HoldsAndCheckoutsSortField,
  SetTabsCounters,
  TabConfig,
} from '../constants';
import { useHoldsAndCheckoutsFetch } from './useHoldsAndCheckoutsFetch';
import { getTabMessages } from './tabContentMessages';

import css from './HoldsAndCheckoutsTabContent.module.scss';

const emptyStateIconMap: Record<HoldsAndCheckoutsTab, IconDefinition> = {
  holds: faBooks,
  checkouts: faBookReader,
  fees: faMoneyBillWave,
};

interface HoldsAndCheckoutsTabContentProps {
  type: HoldsAndCheckoutsTab;
  tabConfig: TabConfig;
  totalRecordsNumber: number;
  setTabsCounters: SetTabsCounters;
}

export const HoldsAndCheckoutsTabContent: FC<
  HoldsAndCheckoutsTabContentProps
> = ({ type, tabConfig, totalRecordsNumber, setTabsCounters }) => {
  const { $t } = useIntl();
  const { isLoggedIn } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { sortField: sortFieldByUrl, sortDirection: sortDirectionByUrl } =
    useQueryParams();
  const { selectConfiguredOption } = useHoldsAndCheckoutsPageConfig();

  const tabContentMessages = getTabMessages(selectConfiguredOption);
  const { sortOptions, queryConfig } = tabConfig;

  const defaultSortParams = useMemo(
    () => ({
      sortField: HoldsAndCheckoutsSortField[sortOptions[0].sortField],
      sortDirection: sortOptions[0].sortDirection as string,
    }),
    [sortOptions]
  );
  const sortByQueryParams = {
    sortField: sortFieldByUrl as string,
    sortDirection: sortDirectionByUrl as string,
  };
  const [sortByParams, setSortByParams] = useState(defaultSortParams);
  const {
    pages,
    itemsStatusesData,
    pageNumber,
    holdsAndCheckoutsQuery,
    setPageNumber,
  } = useHoldsAndCheckoutsFetch({
    sortByParams: sortFieldByUrl ? sortByQueryParams : sortByParams,
    queryConfig,
    setTabsCounters,
  });

  usePaginationItemFocus({
    pageNumber,
    totalPagesNumber: pages?.length,
  });

  useEffect(() => {
    if (!sortFieldByUrl) {
      setSortByParams(defaultSortParams);
    }
  }, [defaultSortParams, sortFieldByUrl]);

  const isHoldsAndCheckoutsDataEmpty =
    itemsStatusesData !== null && isEmpty(itemsStatusesData);

  const handleSortBySelect = option => {
    setSortByParams({
      sortField: option.sortField,
      sortDirection: option.sortDirection,
    });
    navigate(
      generateRouteParams({
        pathname: location.pathname,
        query: {
          sortField: option.sortField,
          sortDirection: option.sortDirection,
        },
      })
    );
  };

  const getResultsCount = () => (
    <ResultCountWithSorting
      recordsCount={totalRecordsNumber}
      countMessage={tabContentMessages[type].resultCount}
      sortOptions={sortOptions}
      sortField={sortByParams.sortField}
      sortDirection={
        sortByQueryParams.sortDirection ?? sortByParams.sortDirection
      }
      onSortChange={handleSortBySelect}
    />
  );

  const renderEmptyTabContent = () => {
    const message = $t(
      tabContentMessages[type][isLoggedIn ? 'noTabResults' : 'promptTitle']
    );

    return (
      <div className={css.emptyState} data-auto="empty-state">
        {getResultsCount()}
        <EmptyRecordsMessage
          className={css.emptyRecordsMessage}
          icon={emptyStateIconMap[type]}
          message={message}
          testId={`no-${type}`}
        >
          {!isLoggedIn && <RedirectToLoginButton />}
        </EmptyRecordsMessage>
      </div>
    );
  };

  if (
    !isLoggedIn ||
    (holdsAndCheckoutsQuery.isSuccess && isHoldsAndCheckoutsDataEmpty)
  ) {
    return renderEmptyTabContent();
  }

  if (pageNumber === 1 && holdsAndCheckoutsQuery.isFetching) {
    return (
      <div data-auto="tab-spinner">
        <Loading loadingMessage={$t(sharedMessages.loadingMessage)} />
      </div>
    );
  }

  if (!holdsAndCheckoutsQuery.isSuccess) {
    return <PageErrorMessage onClick={holdsAndCheckoutsQuery.refetch} />;
  }

  return itemsStatusesData && pages && !isEmpty(pages[0]) ? (
    <>
      {getResultsCount()}
      <ShowMorePagination
        uniqueName={`${type}-list`}
        error={undefined}
        messages={getPageMessages($t, {
          showMoreButtonTitle: $t(tabContentMessages[type].showMoreButtonTitle),
        })}
        isLoading={holdsAndCheckoutsQuery.isFetching}
        totalRecords={totalRecordsNumber}
        pages={pages}
        onLoadPage={() => setPageNumber(pageNumber + 1)}
      >
        {(page, currentPageNumber) => {
          return (
            <Page
              title={$t(sharedMessages.pageNumber, {
                pageNumber: currentPageNumber,
              })}
              key={currentPageNumber}
              number={currentPageNumber}
              uniqueName="holdsAndCheckoutsDivider"
            >
              {page.map((instance, index) => {
                const currentItemStatusData =
                  itemsStatusesData[
                    currentPageNumber * RECORDS_PER_PAGE + index
                  ];

                return (
                  <RecordSummary
                    testId="holds-and-checkouts-item"
                    key={currentItemStatusData.itemId}
                    instance={instance}
                    itemStatusData={currentItemStatusData}
                    placement="holdsAndCheckouts"
                  />
                );
              })}
            </Page>
          );
        }}
      </ShowMorePagination>
    </>
  ) : null;
};
